import { Collections } from '@thom/folio-ui/pocketbase-types'
import type {
	JournEntryTagsResponse,
	JournIssueLinksResponse,
	JournIssueTagsResponse,
	JournTagsResponse,
} from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { keyed } from './request-key'
import { LINK_KIND } from '@thom/folio-domain/core/domain/issue'

type TagLink = { readonly tag: string; readonly expand?: { readonly tag?: JournTagsResponse } }

type Pair = readonly [string, string]

const BATCH = 40

const named = (rows: readonly TagLink[]): readonly string[] =>
	rows.flatMap(row => (row.expand?.tag?.name ? [row.expand.tag.name] : []))

const chunked = (ids: readonly string[]): readonly (readonly string[])[] =>
	Array.from({ length: Math.ceil(ids.length / BATCH) }, (_, at) => ids.slice(at * BATCH, (at + 1) * BATCH))

const orFilter = (field: string, ids: readonly string[]): string =>
	ids.map(id => `${field} = "${id.replaceAll('"', '')}"`).join(' || ')

const groupBy = <T>(items: readonly T[], keyOf: (item: T) => string): ReadonlyMap<string, readonly T[]> =>
	items.reduce((groups, item) => {
		const key = keyOf(item)
		return groups.set(key, [...(groups.get(key) ?? []), item])
	}, new Map<string, readonly T[]>())

const multimap = (pairs: readonly Pair[]): ReadonlyMap<string, readonly string[]> =>
	new Map([...groupBy(pairs, ([key]) => key)].map(([key, group]) => [key, group.map(([, value]) => value)]))

const tagsByTarget = async (
	target: 'issue' | 'entry',
	ids: readonly string[],
): Promise<ReadonlyMap<string, readonly string[]>> => {
	const client = getPocketBaseClient()
	const collection = target === 'issue' ? Collections.JournIssueTags : Collections.JournEntryTags

	const batches = await Promise.all(
		chunked(ids).map(batch =>
			client.collection(collection).getFullList<(JournIssueTagsResponse | JournEntryTagsResponse) & TagLink>(
				keyed(`relations.tags.${target}`, {
					filter: orFilter(target, batch),
					expand: 'tag',
				}),
			),
		),
	)

	const keyOf = (row: unknown): string =>
		target === 'issue' ? (row as { issue: string }).issue : (row as { entry: string }).entry

	return new Map([...groupBy(batches.flat(), keyOf)].map(([key, group]) => [key, named(group)]))
}

type Links = {
	readonly dependsOn: ReadonlyMap<string, readonly string[]>
	readonly relatedTo: ReadonlyMap<string, readonly string[]>
}

const linksOf = async (ids: readonly string[]): Promise<Links> => {
	const client = getPocketBaseClient()

	const batches = await Promise.all(
		chunked(ids).map(batch =>
			client.collection(Collections.JournIssueLinks).getFullList<JournIssueLinksResponse>(
				keyed('relations.links', {
					filter: `(${orFilter('from', batch)}) || (${orFilter('to', batch)})`,
				}),
			),
		),
	)

	const rows = batches.flat()
	const related = rows.filter(row => row.kind === LINK_KIND.RELATES)

	return {
		dependsOn: multimap(rows.filter(row => row.kind === LINK_KIND.BLOCKS).map((row): Pair => [row.to, row.from])),
		relatedTo: multimap(
			related.flatMap((row): Pair[] => [
				[row.from, row.to],
				[row.to, row.from],
			]),
		),
	}
}

export { linksOf, tagsByTarget }
export type { Links }
